import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Bookings API
export const bookingsApi = {
  create: (data) => api.post('/bookings', data),
  getAll: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status?status=${status}`),
  updateEta: (id, data) => api.put(`/bookings/${id}/eta`, data),
  getShippingInstructions: (id) => api.get(`/bookings/${id}/shipping-instructions`),
  submitShippingInstructions: (id, data) => api.put(`/bookings/${id}/shipping-instructions`, data),
  carrierDocuments: (id) => api.get(`/bookings/${id}/carrier-documents`),
  downloadCarrierDocument: (id, type) => api.get(`/bookings/${id}/carrier-documents/${type}`, { responseType: 'blob' }),
  sendCarrierDocument: (id, type) => api.post(`/bookings/${id}/carrier-documents/${type}/send`),
  getVgm: (id) => api.get(`/bookings/${id}/vgm`),
  submitVgm: (id, declarations) => api.put(`/bookings/${id}/vgm`, { declarations }),
  telexRequest: (id, data) => api.post(`/bookings/${id}/telex-release/request`, data || {}),
  telexApprove: (id, data) => api.post(`/bookings/${id}/telex-release/approve`, data || {}),
  telexReject: (id, data) => api.post(`/bookings/${id}/telex-release/reject`, data || {}),
  events: (id) => api.get(`/bookings/${id}/events`),
  updateEvent: (id, index, data) => api.put(`/bookings/${id}/events/${index}`, data),
  containerOperation: (id, data) => api.post(`/bookings/${id}/container-operation`, data),
  documentArchive: (id) => api.get(`/bookings/${id}/document-archive`),
  downloadArchivedDocument: (id, archiveId) => api.get(`/bookings/${id}/document-archive/${archiveId}`, { responseType: 'blob' }),
  demurrage: (id) => api.get(`/bookings/${id}/demurrage`),
  packingPlan: (id) => api.get(`/bookings/${id}/packing-plan`),
  savePackingPlan: (id, containers) => api.put(`/bookings/${id}/packing-plan`, { containers }),
  previewCarrierDocument: (id, type) => api.get(`/bookings/${id}/carrier-documents/${type}/preview`)
};

// Cargo (export) documents workspace
export const cargoApi = {
  workspace: (id) => api.get(`/bookings/${id}/cargo-documents`),
  saveProfile: (id, data) => api.put(`/bookings/${id}/cargo-documents/profile`, data),
  downloadPdf: (id, key) => api.get(`/bookings/${id}/cargo-documents/${key}/pdf`, { responseType: 'blob' }),
  preview: (id, key) => api.get(`/bookings/${id}/cargo-documents/${key}/preview`),
  generateAll: (id) => api.post(`/bookings/${id}/cargo-documents/generate-all`, null, { responseType: 'blob' }),
  setReference: (id, key, data) => api.put(`/bookings/${id}/cargo-documents/${key}/reference`, data),
  upload: (id, key, formData) => api.post(`/bookings/${id}/cargo-documents/${key}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFile: (id, key) => api.get(`/bookings/${id}/cargo-documents/${key}/file`, { responseType: 'blob' }),
  clearExternal: (id, key) => api.delete(`/bookings/${id}/cargo-documents/${key}/external`)
};

export const referenceApi = {
  searchHs: (q) => api.get('/hs-codes', { params: { q } }),
  suggestHs: (params) => api.get('/hs-codes/suggest', { params }),
  equipment: () => api.get('/equipment'),
  generateContainers: (count) => api.get('/containers/generate', { params: { count } }),
  validateContainer: (number) => api.get(`/containers/validate/${encodeURIComponent(number)}`)
};

// Quotes API
export const quotesApi = {
  create: (data) => api.post('/quotes', data),
  getInstant: (data) => api.post('/quotes/instant', data),
  getAll: () => api.get('/quotes')
};

// Tracking API
export const trackingApi = {
  track: (reference) => api.get(`/tracking/${reference}`)
};

// Schedules API
export const schedulesApi = {
  search: (params) => api.get('/schedules', { params }),
  getAll: () => api.get('/schedules')
};

// Ports API
export const portsApi = {
  getAll: (params) => api.get('/ports', { params })
};

// Vessels API
export const vesselsApi = {
  getAll: () => api.get('/vessels')
};

// Admin API
export const adminApi = {
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (userId, data) => api.put(`/admin/users/${userId}/role`, data),
  getStats: () => api.get('/admin/stats')
};

// Agent API
export const agentApi = {
  getStats: () => api.get('/agent/stats'),
  getCustomers: () => api.get('/agent/customers')
};

// Contact API
export const contactApi = {
  submit: (data) => api.post('/contact', data)
};

export default api;
